import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';
import { PERMISSION_CREATE_ASSIGNED_USER_CONTACT } from '../../commons/constants/permissions';
import { AssignedUserContactType, CMSUserContactType } from '../../types/CMSUserContactTypes';
import { PaginationType } from '../../types/CommonTypes';
import { storeName as entityStore } from '../entities/assignedUserContact.entity';
import { storeName as cmsContactEntity } from '../entities/cmsUserContact.entity';
import { RootModel } from '../models';
import { storeName as tourDetail } from './configTour/tourDetail.model';
import { storeName as userStore } from './user.model';

export const storeName = 'assignedUserContacts';

const initialState = {
  ids: [],
  loading: false,
};

export const assignedUserContacts = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    updateLoading: produce((state, payload) => {
      state.loading = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof assignedUserContacts>) => ({
    async filterAsync(payload, state) {
      this.updateLoading(true);
      try {
        const {
          data: { code },
        } = state[tourDetail];
        const result: { content: AssignedUserContactType[] } & PaginationType = await dispatch[
          entityStore
        ].load({ code });
        if (result && result.content) {
          this.setIds(result.content.map((contact) => contact.id));
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to filter');
      } finally {
        this.updateLoading(false);
      }
    },
    async assignContacts(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        const { targetKeys, defaultContact } = payload;
        const { ids: cmsContactIds } = state[cmsContactEntity];
        const { ids: assignedContactIds } = state[entityStore];
        const cmsContacts: CMSUserContactType[] = Object.values(cmsContactIds);
        const assignedKeys: string[] = Object.values(assignedContactIds).map(
          (assigned: AssignedUserContactType) => {
            return assigned.userContact.code;
          }
        );
        const contactKeys = [];
        const cmsContactKeys = [];
        targetKeys.forEach((key) => {
          if (cmsContacts.find((ct) => ct.code === key)) {
            cmsContactKeys.push(key);
          } else {
            contactKeys.push({ id: Number(key) });
          }
        });
        const canCreateAssignedUserContact = await dispatch[userStore].hasPermission(
          PERMISSION_CREATE_ASSIGNED_USER_CONTACT
        );

        let defaultContactCode;
        if (defaultContact) {
          const { code: contactCode, assignedCode } = defaultContact;
          if (cmsContactKeys.includes(contactCode) && assignedCode) {
            defaultContactCode = assignedCode;
          }
        }

        if (canCreateAssignedUserContact) {
          const {
            data: { code: virtualTourCode },
          } = state[tourDetail];
          await dispatch[entityStore].assign({ virtualTourCode, contactCodes: cmsContactKeys });
        } else {
          const assignedString = assignedKeys.sort().join('-');
          const cmsContactString = cmsContactKeys.sort().join('-');
          if (assignedString !== cmsContactString) {
            throw new Error('Failed to cms assign user contacts');
          }
        }
        await dispatch[tourDetail].saveTourContacts({
          defaultContactCode,
          userContacts: contactKeys,
        });
      } catch (err) {
        console.error(err);
        message.error('Failed to assign user contacts');
      } finally {
        this.updateLoading(false);
      }
    },
  }),
});

export default assignedUserContacts;
